import { useState, useRef } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from 'recharts';
import DashboardLayout from '@/pages/dashboard/components/DashboardLayout';
import {
  useRevenueReport,
  useSalesReport,
  useDiscountsReport,
  useActivityReport,
  useUtilizationSummaryReport,
  useUtilizationDetailReport,
  useStockBatchReport,
  useLocations,
} from '@/hooks/useQueries';
import type {
  RevenueReport,
  UtilizationReport,
  UtilizationDetailRow,
  SalesSummaryRow,
  SalesDetailRow,
  SalesByCategoryRow,
  DiscountReportRow,
} from '@/types/index.ts';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const today = new Date().toISOString().split('T')[0];
const thirtyDaysAgo = new Date(Date.now() - 30 * 864e5).toISOString().split('T')[0];

function fmtJmd(n: number) {
  return `JMD ${Number(n).toLocaleString('en-JM', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtDate(d: string) {
  return new Date(d + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

type ReportType = 'revenue' | 'sales' | 'discounts' | 'activity' | 'utilization' | 'stock_batches';

const REPORT_OPTIONS: { value: ReportType; label: string; icon: string }[] = [
  { value: 'revenue',       label: 'Revenue Report',       icon: 'ri-money-dollar-circle-line' },
  { value: 'sales',         label: 'Sales Report',         icon: 'ri-shopping-bag-line' },
  { value: 'discounts',     label: 'Discounts Report',     icon: 'ri-percent-line' },
  { value: 'activity',      label: 'Activity Report',      icon: 'ri-list-check-2' },
  { value: 'utilization',   label: 'Utilization Report',   icon: 'ri-bar-chart-grouped-line' },
  { value: 'stock_batches', label: 'Stock Batch Report',   icon: 'ri-stack-line' },
];

// ─── Pagination ───────────────────────────────────────────────────────────────
function Pagination({ page, total, onChange }: { page: number; total: number; onChange: (p: number) => void }) {
  if (total <= 1) return null;
  const pages: (number | '...')[] = [];
  if (total <= 7) { for (let i = 1; i <= total; i++) pages.push(i); }
  else if (page <= 4) { for (let i = 1; i <= 5; i++) pages.push(i); pages.push('...'); pages.push(total); }
  else if (page >= total - 3) { pages.push(1); pages.push('...'); for (let i = total - 4; i <= total; i++) pages.push(i); }
  else { pages.push(1); pages.push('...'); for (let i = page - 1; i <= page + 1; i++) pages.push(i); pages.push('...'); pages.push(total); }
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3 flex items-center justify-between">
      <p className="text-xs text-gray-500">Page {page} of {total}</p>
      <div className="flex items-center gap-1">
        <button onClick={() => onChange(1)} disabled={page === 1} className="px-2.5 py-1.5 text-sm border border-gray-300 disabled:opacity-40 hover:bg-gray-50">«</button>
        <button onClick={() => onChange(Math.max(1, page - 1))} disabled={page === 1} className="px-2.5 py-1.5 text-sm border border-gray-300 disabled:opacity-40 hover:bg-gray-50">‹</button>
        {pages.map((p, i) => p === '...'
          ? <span key={`e${i}`} className="px-2 text-sm text-gray-400">…</span>
          : <button key={p} onClick={() => onChange(p as number)} className={`px-3 py-1.5 text-sm border ${page === p ? 'bg-[#0997a4] text-white border-[#0997a4]' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>{p}</button>
        )}
        <button onClick={() => onChange(Math.min(total, page + 1))} disabled={page === total} className="px-2.5 py-1.5 text-sm border border-gray-300 disabled:opacity-40 hover:bg-gray-50">›</button>
        <button onClick={() => onChange(total)} disabled={page === total} className="px-2.5 py-1.5 text-sm border border-gray-300 disabled:opacity-40 hover:bg-gray-50">»</button>
      </div>
    </div>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────
function EmptyState({ generated }: { generated: boolean }) {
  return generated ? (
    <div className="bg-white border border-gray-200 rounded-lg py-16 text-center">
      <p className="text-sm text-gray-400">No data found for the selected period.</p>
      </div>
  ) : (
    <div className="bg-white border border-dashed border-gray-300 rounded-lg py-20 text-center">
      <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <i className="ri-file-chart-line text-2xl text-gray-400" />
      </div>
      <p className="text-sm font-medium text-gray-600">Select a report and click <span className="text-[#0997a4] font-semibold">Generate</span></p>
    </div>
  );
}

// ─── Committed params (only change on Generate) ───────────────────────────────
interface CommittedParams {
  type: ReportType;
  dateFrom: string;
  dateTo: string;
  groupBy: 'day' | 'month';
  activityType: 'all' | 'receive' | 'issue';
  locationId: string;
  itemSearch: string;
  batchSearch: string;
  includeEmpty: boolean;
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function Reports() {
  // ── Pending (what the user is editing) ────────────────────────────────────
  const [reportType,   setReportType]   = useState<ReportType>('revenue');
  const [dateFrom,     setDateFrom]     = useState(thirtyDaysAgo);
  const [dateTo,       setDateTo]       = useState(today);
  const [groupBy,      setGroupBy]      = useState<'day' | 'month'>('day');
  const [activityType, setActivityType] = useState<'all' | 'receive' | 'issue'>('all');
  const [locationId,   setLocationId]   = useState('');

  // ── Sub-view state (no Generate needed — lazy fetch, instant cache return) ─
  const [salesView,  setSalesView]  = useState<'summary' | 'detail' | 'by_category'>('summary');
  const [utilView,   setUtilView]   = useState<'summary' | 'detail'>('summary');

  // ── Stock batch filter state ───────────────────────────────────────────────
  const [itemSearch,   setItemSearch]   = useState('');
  const [batchSearch,  setBatchSearch]  = useState('');
  const [includeEmpty, setIncludeEmpty] = useState(false);

  // ── Per-view page numbers ─────────────────────────────────────────────────
  const [salesPage,       setSalesPage]       = useState(1);
  const [discountsPage,   setDiscountsPage]   = useState(1);
  const [activityPage,    setActivityPage]    = useState(1);
  const [utilDetailPage,  setUtilDetailPage]  = useState(1);
  const [stockBatchPage,  setStockBatchPage]  = useState(1);

  // ── Committed params — drives all queries ─────────────────────────────────
  const [committed, setCommitted] = useState<CommittedParams | null>(null);

  const chartRef = useRef<HTMLDivElement>(null);

  // ── Locations for the Utilization + Stock Batch filters ──────────────────
  const locationsQuery = useLocations({ per_page: 100, enabled: reportType === 'utilization' || committed?.type === 'utilization' || reportType === 'stock_batches' || committed?.type === 'stock_batches' });
  const locations: { id: number; name: string }[] = (locationsQuery.data as any)?.data ?? (locationsQuery.data as any) ?? [];

  // ── Derive query params from committed state ───────────────────────────────
  const isRevenue      = committed?.type === 'revenue';
  const isSales        = committed?.type === 'sales';
  const isDiscounts    = committed?.type === 'discounts';
  const isActivity     = committed?.type === 'activity';
  const isUtilization  = committed?.type === 'utilization';
  const isStockBatches = committed?.type === 'stock_batches';

  const revenueQuery = useRevenueReport(
    isRevenue ? { date_from: committed!.dateFrom, date_to: committed!.dateTo, group_by: committed!.groupBy } : null
  );
  // Each sales sub-view is a separate cached query; only enabled when user is on that view
  const salesQuery = useSalesReport(
    isSales ? { date_from: committed!.dateFrom, date_to: committed!.dateTo, view: salesView, per_page: 50, page: salesPage } : null
  );
  const discountsQuery = useDiscountsReport(
    isDiscounts ? { date_from: committed!.dateFrom, date_to: committed!.dateTo, per_page: 50, page: discountsPage } : null
  );
  const activityQuery = useActivityReport(
    isActivity ? {
      date_from: committed!.dateFrom,
      date_to: committed!.dateTo,
      type: committed!.activityType === 'all' ? undefined : committed!.activityType,
      per_page: 50,
      page: activityPage,
    } : null
  );
  const utilizationQuery = useUtilizationSummaryReport(
    isUtilization && utilView === 'summary'
      ? { date_from: committed!.dateFrom, date_to: committed!.dateTo, location_id: committed!.locationId ? Number(committed!.locationId) : undefined }
      : null
  );
  const utilizationDetailQuery = useUtilizationDetailReport(
    isUtilization && utilView === 'detail'
      ? { date_from: committed!.dateFrom, date_to: committed!.dateTo, location_id: committed!.locationId ? Number(committed!.locationId) : undefined, per_page: 50, page: utilDetailPage }
      : null
  );
  const stockBatchQuery = useStockBatchReport(
    isStockBatches ? {
      location_id:  committed!.locationId  ? Number(committed!.locationId)  : undefined,
      item_search:  committed!.itemSearch  || undefined,
      batch_number: committed!.batchSearch || undefined,
      include_empty: committed!.includeEmpty,
      page:    stockBatchPage,
      per_page: 50,
    } : null
  );

  // ── Resolve active query for loading / data ───────────────────────────────
  const activeQuery = isRevenue      ? revenueQuery
    : isSales        ? salesQuery
    : isDiscounts    ? discountsQuery
    : isActivity     ? activityQuery
    : isUtilization && utilView === 'detail' ? utilizationDetailQuery
    : isUtilization  ? utilizationQuery
    : isStockBatches ? stockBatchQuery
    : null;

  const loading   = activeQuery?.isFetching ?? false;
  const generated = committed !== null;

  // ── Data per view ─────────────────────────────────────────────────────────
  const revenueData     = revenueQuery.data       as RevenueReport | undefined;
  const salesData       = salesQuery.data         as any;
  const discountsData   = discountsQuery.data     as any;
  const activityData    = activityQuery.data      as any;
  const utilizationData = utilizationQuery.data   as UtilizationReport | undefined;
  const utilDetailData  = utilizationDetailQuery.data as any;
  const stockBatchData  = stockBatchQuery.data    as any;

  // ── Page totals ───────────────────────────────────────────────────────────
  const salesTotalPages       = (salesData as any)?.meta?.last_page       ?? 1;
  const discountsTotalPages   = (discountsData as any)?.meta?.last_page   ?? 1;
  const activityTotalPages    = (activityData as any)?.meta?.last_page    ?? 1;
  const utilDetailTotalPages  = (utilDetailData as any)?.meta?.last_page  ?? 1;
  const stockBatchTotalPages  = (stockBatchData as any)?.meta?.last_page  ?? 1;

  // ── Generate: commit pending params and reset pages ───────────────────────
  const handleGenerate = () => {
    setCommitted({ type: reportType, dateFrom, dateTo, groupBy, activityType, locationId, itemSearch, batchSearch, includeEmpty });
    setSalesPage(1);
    setDiscountsPage(1);
    setActivityPage(1);
    setUtilDetailPage(1);
    setStockBatchPage(1);
  };

  const handleReportChange = (type: ReportType) => {
    setReportType(type);
    setCommitted(null);
    setSalesView('summary');
    setUtilView('summary');
    setItemSearch('');
    setBatchSearch('');
    setIncludeEmpty(false);
  };

  // ── Chart image export ────────────────────────────────────────────────────
  const exportChartAsImage = () => {
    const svgEl = chartRef.current?.querySelector('svg');
    if (!svgEl) return;
    const svgStr = new XMLSerializer().serializeToString(svgEl);
    const canvas = document.createElement('canvas');
    canvas.width = svgEl.clientWidth || 900;
    canvas.height = svgEl.clientHeight || 300;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, 0, 0);
      const link = document.createElement('a');
      link.download = `revenue-chart-${dateFrom}-${dateTo}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgStr);
  };

  // ── CSV export ────────────────────────────────────────────────────────────
  const exportCsv = (filename: string, rows: (string | number)[][]) => {
    const csv = rows.map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const link = document.createElement('a');
    link.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    link.download = filename;
    link.click();
  };

  const handleExport = () => {
    if (!generated) return;
    const df = committed!.dateFrom, dt = committed!.dateTo;
    if (reportType === 'revenue' && revenueData) {
      const d = revenueData;
      exportCsv(`revenue-${df}-${dt}.csv`, [
        ['Period', 'Transactions', 'Gross Revenue (JMD)'],
        ...d.series.map(r => [r.period, r.transactions, r.gross.toFixed(2)]),
        [],
        ['Gross Revenue', d.summary.gross_revenue.toFixed(2)],
        ['Total Discounts', d.summary.total_discounts.toFixed(2)],
        ['Net Revenue', d.summary.net_revenue.toFixed(2)],
        ['COGS', (d.summary.cogs ?? 0).toFixed(2)],
        ['Gross Profit', (d.summary.gross_profit ?? 0).toFixed(2)],
        ['Profit Margin (%)', (d.summary.profit_margin ?? 0).toFixed(2)],
      ]);
      exportChartAsImage();
    } else if (reportType === 'sales' && salesData) {
      if (salesView === 'summary') {
        exportCsv(`sales-summary-${df}-${dt}.csv`, [
          ['Date', 'Invoice Ref', 'Patient', 'MRN', 'Type', 'Items', 'Gross (JMD)', 'Discount (JMD)', 'Net (JMD)'],
          ...(salesData.data ?? []).map((r: SalesSummaryRow) => [r.date, r.visit_ref, r.patient_name, r.patient_identifier, r.visit_type, r.items_count, r.gross_total.toFixed(2), r.discount_total.toFixed(2), r.net_total.toFixed(2)]),
        ]);
      } else if (salesView === 'detail') {
        exportCsv(`sales-detail-${df}-${dt}.csv`, [
          ['Date', 'Invoice', 'Patient', 'MRN', 'Type', 'Item', 'Category', 'Qty', 'Unit Price', 'Line Gross', 'Disc. Type', 'Disc. Value', 'Disc. Amount', 'Net Line'],
          ...(salesData.data ?? []).map((r: SalesDetailRow) => [r.date, r.visit_ref, r.patient_name, r.patient_identifier, r.visit_type, r.item_name, r.category, r.quantity, r.unit_price.toFixed(2), r.line_gross.toFixed(2), r.discount_type ?? '', r.discount_value ?? '', r.discount_amount.toFixed(2), r.net_line.toFixed(2)]),
        ]);
      } else {
        exportCsv(`sales-by-category-${df}-${dt}.csv`, [
          ['Category', 'Invoices', 'Total Qty', 'Gross Revenue', 'Discounts', 'Net Revenue'],
          ...(salesData.rows ?? []).map((r: SalesByCategoryRow) => [r.category, r.visit_count, r.total_qty, r.gross_revenue.toFixed(2), r.discount_total.toFixed(2), r.net_revenue.toFixed(2)]),
        ]);
      }
    } else if (reportType === 'discounts' && discountsData) {
      exportCsv(`discounts-${df}-${dt}.csv`, [
        ['Date', 'Invoice', 'Patient', 'MRN', 'Type', 'Item', 'Category', 'Qty', 'Unit Price', 'Line Gross', 'Disc. Type', 'Disc. Value', 'Disc. Amount', 'Net Line'],
        ...(discountsData.data ?? []).map((r: DiscountReportRow) => [r.date, r.visit_ref, r.patient_name, r.patient_identifier, r.visit_type, r.item_name, r.category, r.quantity, r.unit_price.toFixed(2), r.line_gross.toFixed(2), r.discount_type, r.discount_value, r.discount_amount.toFixed(2), r.net_line.toFixed(2)]),
      ]);
    } else if (reportType === 'activity' && activityData) {
      exportCsv(`activity-${df}-${dt}.csv`, [
        ['Date', 'Type', 'Reference', 'User', 'Location', 'Items', 'Total Qty'],
        ...(activityData.data ?? activityData.data?.data ?? []).map((r: any) => {
          const items = r.items || [];
          return [r.created_at?.slice(0, 10) ?? '', r.type, r.reference_number ?? '', r.user?.name ?? '', r.location?.name ?? '', items.map((i: any) => i.item?.name || i.name || '').join('; '), items.reduce((s: number, i: any) => s + (i.quantity || 0), 0)];
        }),
      ]);
    } else if (reportType === 'utilization') {
      if (utilView === 'summary' && utilizationData) {
        exportCsv(`utilization-summary-${df}-${dt}.csv`, [
          ['Item', 'Category', 'Unit', 'Total Issued'],
          ...(utilizationData.items ?? []).map(r => [r.item_name, r.category ?? '', r.item_unit, r.grand_total]),
        ]);
      } else if (utilView === 'detail' && utilDetailData) {
        exportCsv(`utilization-detail-${df}-${dt}.csv`, [
          ['Date', 'Item', 'Category', 'Qty', 'Location', 'Patient', 'MRN', 'Visit', 'Type'],
          ...(utilDetailData.data ?? []).map((r: UtilizationDetailRow) => [r.date, r.item_name, r.category ?? '', r.quantity, r.location_name, r.patient_name, r.patient_identifier, r.visit_reference, r.visit_type]),
        ]);
      }
    } else if (reportType === 'stock_batches' && stockBatchData) {
      exportCsv(`stock-batches.csv`, [
        ['Item', 'Category', 'Location', 'Batch #', 'Expiry Date', 'Received', 'Remaining', 'Unit Price (JMD)', 'Stock Value (JMD)'],
        ...(stockBatchData.data ?? []).map((r: any) => [
          r.item_name, r.category, r.location_name, r.batch_number,
          r.expiry_date ?? '', r.quantity_received, r.quantity_remaining,
          r.unit_price.toFixed(2), r.stock_value.toFixed(2),
        ]),
      ]);
    }
  };

  const hasData = generated && (activeQuery?.isSuccess ?? false);
  const currentReport = REPORT_OPTIONS.find(r => r.value === reportType)!;

  // Compact segment button classes
  const seg = (active: boolean) =>
    `px-3 py-1.5 text-xs font-medium whitespace-nowrap transition-colors ${active ? 'bg-[#0997a4] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`;

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto space-y-4 px-4 sm:px-6 lg:pr-4">

        {/* ── Compact Control Bar (single row, never wraps) ── */}
        <div className="bg-white border border-gray-200 rounded-lg px-4 py-2.5 flex items-center gap-2 overflow-x-auto min-w-0">

          {/* Report dropdown */}
          <div className="relative flex-shrink-0">
            <i className={`${currentReport.icon} absolute left-2.5 top-1/2 -translate-y-1/2 text-[#0997a4] text-xs pointer-events-none`} />
            <select
              value={reportType}
              onChange={e => handleReportChange(e.target.value as ReportType)}
              className="appearance-none pl-7 pr-6 py-1.5 text-xs font-medium border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-[#0997a4] cursor-pointer"
            >
              {REPORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <i className="ri-arrow-down-s-line absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none" />
          </div>

          {/* Divider */}
          <div className="h-5 w-px bg-gray-200 flex-shrink-0" />

          {/* Date range — hidden for stock_batches (snapshot, not time-series) */}
          {reportType !== 'stock_batches' && (
            <>
              <input type="date" value={dateFrom} max={dateTo || today} onChange={e => setDateFrom(e.target.value)}
                className="w-32 px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#0997a4] flex-shrink-0" />
              <span className="text-xs text-gray-400 flex-shrink-0">to</span>
              <input type="date" value={dateTo} min={dateFrom} max={today} onChange={e => setDateTo(e.target.value)}
                className="w-32 px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#0997a4] flex-shrink-0" />
            </>
          )}

          {/* Report-specific sub-filters */}
          {reportType === 'revenue' && (
            <>
              <div className="h-5 w-px bg-gray-200 flex-shrink-0" />
              <div className="flex rounded-lg overflow-hidden border border-gray-300 flex-shrink-0">
                <button onClick={() => setGroupBy('day')}   className={seg(groupBy === 'day')}>Daily</button>
                <button onClick={() => setGroupBy('month')} className={seg(groupBy === 'month')}>Monthly</button>
              </div>
            </>
          )}

          {reportType === 'sales' && (
            <>
              <div className="h-5 w-px bg-gray-200 flex-shrink-0" />
              <div className="flex rounded-lg overflow-hidden border border-gray-300 flex-shrink-0">
                <button onClick={() => setSalesView('summary')}     className={seg(salesView === 'summary')}>Summary</button>
                <button onClick={() => setSalesView('detail')}      className={seg(salesView === 'detail')}>Data</button>
                <button onClick={() => setSalesView('by_category')} className={seg(salesView === 'by_category')}>By Category</button>
              </div>
            </>
          )}

          {reportType === 'activity' && (
            <>
              <div className="h-5 w-px bg-gray-200 flex-shrink-0" />
              <select value={activityType} onChange={e => setActivityType(e.target.value as any)}
                className="px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#0997a4] bg-white flex-shrink-0">
                <option value="all">All Types</option>
                <option value="receive">Received</option>
                <option value="issue">Issued</option>
              </select>
            </>
          )}

          {reportType === 'utilization' && (
            <>
              <div className="h-5 w-px bg-gray-200 flex-shrink-0" />
              <select value={locationId} onChange={e => setLocationId(e.target.value)}
                className="px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#0997a4] bg-white max-w-[140px] flex-shrink-0">
                <option value="">All Locations</option>
                {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
              <div className="flex rounded-lg overflow-hidden border border-gray-300 flex-shrink-0">
                <button onClick={() => setUtilView('summary')} className={seg(utilView === 'summary')}>Summary</button>
                <button onClick={() => setUtilView('detail')}  className={seg(utilView === 'detail')}>Data</button>
              </div>
            </>
          )}

          {reportType === 'stock_batches' && (
            <>
              <div className="h-5 w-px bg-gray-200 flex-shrink-0" />
              <select value={locationId} onChange={e => setLocationId(e.target.value)}
                className="px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#0997a4] bg-white max-w-[140px] flex-shrink-0">
                <option value="">All Locations</option>
                {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
              <input
                type="text"
                placeholder="Search item…"
                value={itemSearch}
                onChange={e => setItemSearch(e.target.value)}
                className="w-28 px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#0997a4] flex-shrink-0"
              />
              <input
                type="text"
                placeholder="Batch #…"
                value={batchSearch}
                onChange={e => setBatchSearch(e.target.value)}
                className="w-24 px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#0997a4] flex-shrink-0"
              />
              <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer flex-shrink-0 whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={includeEmpty}
                  onChange={e => setIncludeEmpty(e.target.checked)}
                  className="rounded border-gray-300 text-[#0997a4] focus:ring-[#0997a4]"
                />
                Inc. empty
              </label>
            </>
          )}

          {/* Push actions to end */}
          <div className="flex-1 min-w-2" />

          <button onClick={handleGenerate} disabled={loading}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-[#0997a4] text-white text-xs font-semibold rounded-lg hover:bg-[#087d89] transition-colors disabled:opacity-60 whitespace-nowrap flex-shrink-0">
            {loading ? <><i className="ri-loader-4-line animate-spin" />Generating…</> : <><i className="ri-play-circle-line" />Generate</>}
          </button>
          <button onClick={handleExport} disabled={!hasData}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-40 whitespace-nowrap flex-shrink-0">
            <i className="ri-download-line" />Export
          </button>
        </div>

        {/* ── Report Content ── */}
        {loading ? (
          <div className="bg-white border border-gray-200 rounded-lg py-24 flex flex-col items-center gap-3">
            <div className="w-10 h-10 rounded-full border-2 border-[#0997a4] border-t-transparent animate-spin" />
            <p className="text-sm text-gray-500">Generating report…</p>
          </div>
        ) : (
          <>
            {reportType === 'revenue'       && <RevenueView      data={revenueData ?? null}     generated={generated} chartRef={chartRef} />}
            {reportType === 'sales'         && <SalesView        data={salesData}               view={salesView}  generated={generated} page={salesPage}       totalPages={salesTotalPages}       onPageChange={p => setSalesPage(p)} />}
            {reportType === 'discounts'     && <DiscountsView    data={discountsData}            generated={generated} page={discountsPage}  totalPages={discountsTotalPages}   onPageChange={p => setDiscountsPage(p)} />}
            {reportType === 'activity'      && <ActivityView     data={activityData}             generated={generated} page={activityPage}   totalPages={activityTotalPages}    onPageChange={p => setActivityPage(p)} />}
            {reportType === 'utilization'   && <UtilizationView  data={utilView === 'summary' ? (utilizationData ?? null) : utilDetailData} view={utilView} generated={generated} page={utilDetailPage} totalPages={utilDetailTotalPages} onPageChange={p => setUtilDetailPage(p)} />}
            {reportType === 'stock_batches' && <StockBatchesView data={stockBatchData}           generated={generated} page={stockBatchPage} totalPages={stockBatchTotalPages}  onPageChange={p => setStockBatchPage(p)} />}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

// ─── Revenue ──────────────────────────────────────────────────────────────────
function RevenueView({ data, generated, chartRef }: {
  data: RevenueReport | null; generated: boolean;
  chartRef: React.RefObject<HTMLDivElement | null>;
}) {
  if (!data) return <EmptyState generated={generated} />;

  const chartData = data.series.map(r => ({
    name: r.period.slice(data.group_by === 'month' ? 0 : 5),
    gross: r.gross,
    transactions: r.transactions,
    fullPeriod: r.period,
  }));

  return (
    <div className="space-y-4">
          {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {[
          { label: 'Gross Revenue',      value: fmtJmd(data.summary.gross_revenue),          icon: 'ri-funds-line',               color: 'text-[#0997a4]'   },
          { label: 'Total Discounts',    value: fmtJmd(data.summary.total_discounts),         icon: 'ri-percent-line',             color: 'text-amber-600'   },
          { label: 'Net Revenue',        value: fmtJmd(data.summary.net_revenue),             icon: 'ri-money-dollar-circle-line', color: 'text-emerald-600' },
          { label: 'Cost of Goods Sold', value: fmtJmd(data.summary.cogs ?? 0),              icon: 'ri-price-tag-3-line',         color: 'text-rose-600'    },
          { label: 'Gross Profit',       value: fmtJmd(data.summary.gross_profit ?? 0),       icon: 'ri-line-chart-line',          color: 'text-violet-600'  },
          { label: 'Profit Margin',      value: `${(data.summary.profit_margin ?? 0).toFixed(1)}%`, icon: 'ri-bar-chart-line',    color: (data.summary.profit_margin ?? 0) >= 0 ? 'text-emerald-600' : 'text-red-600' },
        ].map(c => (
          <div key={c.label} className="bg-white border border-gray-200 rounded-lg p-4 flex items-center gap-3">
            <div className={`text-2xl ${c.color}`}><i className={c.icon} /></div>
                <div>
              <p className="text-xs text-gray-500">{c.label}</p>
              <p className="text-sm font-bold text-gray-900 mt-0.5">{c.value}</p>
                </div>
              </div>
            ))}
          </div>

      {/* Line chart */}
      {chartData.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">
            Gross Revenue — {data.group_by === 'day' ? 'Daily' : 'Monthly'} Trend
          </h3>
          <div ref={chartRef} className="w-full h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#9ca3af' }} tickLine={false} />
                <YAxis
                  tick={{ fontSize: 10, fill: '#9ca3af' }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={v => `${(v / 1000).toFixed(0)}k`}
                />
                <Tooltip
                  formatter={(value: number) => [fmtJmd(value), 'Gross Revenue']}
                  labelFormatter={(_: any, payload: readonly any[]) => payload?.[0]?.payload?.fullPeriod ?? ''}
                  contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                />
                <Line
                  type="monotone"
                  dataKey="gross"
                  stroke="#0997a4"
                  strokeWidth={2.5}
                  dot={{ r: 3, fill: '#0997a4', strokeWidth: 0 }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Period table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              {['Period', 'Transactions', 'Gross Revenue'].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.series.length > 0 ? data.series.map(r => (
              <tr key={r.period} className="hover:bg-gray-50">
                <td className="px-4 py-2.5 font-mono text-xs text-gray-900">{r.period}</td>
                <td className="px-4 py-2.5 text-gray-700">{r.transactions}</td>
                <td className="px-4 py-2.5 font-medium text-gray-900">{fmtJmd(r.gross)}</td>
              </tr>
            )) : (
              <tr><td colSpan={3} className="px-4 py-8 text-center text-sm text-gray-400">No revenue data in this period.</td></tr>
            )}
          </tbody>
          {data.series.length > 0 && (
            <tfoot className="bg-gray-50 border-t-2 border-gray-200">
              <tr>
                <td colSpan={2} className="px-4 py-2.5 text-xs font-semibold text-gray-600 uppercase">Total</td>
                <td className="px-4 py-2.5 font-bold text-gray-900">{fmtJmd(data.summary.gross_revenue)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
                      </div>
                    );
}

// ─── Sales ────────────────────────────────────────────────────────────────────
function SalesView({ data, view, generated, page, totalPages, onPageChange }: {
  data: any; view: 'summary' | 'detail' | 'by_category'; generated: boolean;
  page: number; totalPages: number; onPageChange: (p: number) => void;
}) {
  if (!data) return <EmptyState generated={generated} />;

  if (view === 'summary') {
    const rows: SalesSummaryRow[] = data.data ?? [];
    const totals = data.totals ?? {};
    return (
      <div className="space-y-3">
        {(totals.visit_count > 0 || rows.length > 0) && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Total Invoices',  value: String(totals.visit_count ?? rows.length), color: 'text-[#0997a4]'   },
              { label: 'Gross Sales',     value: fmtJmd(totals.gross_total ?? 0),           color: 'text-gray-800'    },
              { label: 'Total Discounts', value: fmtJmd(totals.discount_total ?? 0),        color: 'text-amber-600'   },
              { label: 'Net Sales',       value: fmtJmd(totals.net_total ?? 0),             color: 'text-emerald-600' },
            ].map(c => (
              <div key={c.label} className="bg-white border border-gray-200 rounded-lg p-3">
                <p className="text-xs text-gray-500">{c.label}</p>
                <p className={`text-sm font-bold mt-0.5 ${c.color}`}>{c.value}</p>
              </div>
            ))}
            </div>
          )}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {['Date', 'Invoice Ref', 'Patient', 'MRN', 'Type', 'Items', 'Gross', 'Discount', 'Net Total'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.length > 0 ? rows.map((r, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap">{fmtDate(r.date)}</td>
                    <td className="px-4 py-2.5 font-mono text-xs text-[#0997a4] whitespace-nowrap">{r.visit_ref}</td>
                    <td className="px-4 py-2.5 text-gray-900">{r.patient_name}</td>
                    <td className="px-4 py-2.5 text-xs text-gray-500">{r.patient_identifier || '—'}</td>
                    <td className="px-4 py-2.5">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${r.visit_type === 'inpatient' ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>{r.visit_type}</span>
                    </td>
                    <td className="px-4 py-2.5 text-gray-700">{r.items_count}</td>
                    <td className="px-4 py-2.5 text-gray-900">{fmtJmd(r.gross_total)}</td>
                    <td className="px-4 py-2.5 text-amber-600">{r.discount_total > 0 ? fmtJmd(r.discount_total) : <span className="text-gray-300">—</span>}</td>
                    <td className="px-4 py-2.5 font-semibold text-gray-900">{fmtJmd(r.net_total)}</td>
                  </tr>
                )) : (
                  <tr><td colSpan={9} className="px-4 py-8 text-center text-sm text-gray-400">No sales in this period.</td></tr>
                )}
              </tbody>
              {rows.length > 0 && (
                <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                  <tr>
                    <td colSpan={6} className="px-4 py-2.5 text-xs font-semibold text-gray-600 uppercase">Totals</td>
                    <td className="px-4 py-2.5 font-semibold text-gray-900">{fmtJmd(totals.gross_total ?? 0)}</td>
                    <td className="px-4 py-2.5 font-semibold text-amber-600">{fmtJmd(totals.discount_total ?? 0)}</td>
                    <td className="px-4 py-2.5 font-bold text-gray-900">{fmtJmd(totals.net_total ?? 0)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
        <Pagination page={page} total={totalPages} onChange={onPageChange} />
      </div>
    );
  }

  if (view === 'detail') {
    const rows: SalesDetailRow[] = data.data ?? [];
    return (
      <div className="space-y-3">
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {['Date', 'Invoice', 'Patient', 'Item', 'Category', 'Qty', 'Unit Price', 'Line Gross', 'Discount', 'Net Line'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.length > 0 ? rows.map((r, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap">{fmtDate(r.date)}</td>
                    <td className="px-4 py-2.5 font-mono text-xs text-[#0997a4] whitespace-nowrap">{r.visit_ref}</td>
                    <td className="px-4 py-2.5 text-gray-900 whitespace-nowrap">{r.patient_name}</td>
                    <td className="px-4 py-2.5 font-medium text-gray-900">{r.item_name}</td>
                    <td className="px-4 py-2.5 text-xs text-gray-500">{r.category}</td>
                    <td className="px-4 py-2.5 text-gray-700">{r.quantity}</td>
                    <td className="px-4 py-2.5 text-gray-700">{fmtJmd(r.unit_price)}</td>
                    <td className="px-4 py-2.5 text-gray-900">{fmtJmd(r.line_gross)}</td>
                    <td className="px-4 py-2.5">
                      {r.discount_amount > 0
                        ? <span className="text-amber-600 text-xs">-{fmtJmd(r.discount_amount)}{r.discount_type === 'percentage' && <span className="text-gray-400 ml-1">({r.discount_value}%)</span>}</span>
                        : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-2.5 font-semibold text-gray-900">{fmtJmd(r.net_line)}</td>
                  </tr>
                )) : (
                  <tr><td colSpan={10} className="px-4 py-8 text-center text-sm text-gray-400">No line items in this period.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <Pagination page={page} total={totalPages} onChange={onPageChange} />
      </div>
    );
  }

  // By category — backend returns { success, view, rows, totals } directly
  const rows: SalesByCategoryRow[] = data.rows ?? [];
  const totals = data.totals ?? {};

  return (
    <div className="space-y-3">
      {rows.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Gross Revenue',   value: fmtJmd(totals.gross_revenue ?? 0),  color: 'text-[#0997a4]'   },
            { label: 'Total Discounts', value: fmtJmd(totals.discount_total ?? 0), color: 'text-amber-600'   },
            { label: 'Net Revenue',     value: fmtJmd(totals.net_revenue ?? 0),    color: 'text-emerald-600' },
          ].map(c => (
            <div key={c.label} className="bg-white border border-gray-200 rounded-lg p-3">
              <p className="text-xs text-gray-500">{c.label}</p>
              <p className={`text-sm font-bold mt-0.5 ${c.color}`}>{c.value}</p>
            </div>
          ))}
        </div>
      )}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              {['Category', 'Invoices', 'Total Qty', 'Gross Revenue', 'Discounts', 'Net Revenue'].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.length > 0 ? rows.map((r, i) => (
              <tr key={i} className="hover:bg-gray-50">
                <td className="px-4 py-2.5 font-medium text-gray-900">{r.category}</td>
                <td className="px-4 py-2.5 text-gray-700">{r.visit_count}</td>
                <td className="px-4 py-2.5 text-gray-700">{r.total_qty}</td>
                <td className="px-4 py-2.5 text-gray-900">{fmtJmd(r.gross_revenue)}</td>
                <td className="px-4 py-2.5 text-amber-600">{r.discount_total > 0 ? fmtJmd(r.discount_total) : <span className="text-gray-300">—</span>}</td>
                <td className="px-4 py-2.5 font-semibold text-gray-900">{fmtJmd(r.net_revenue)}</td>
              </tr>
            )) : (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-400">No category data in this period.</td></tr>
            )}
          </tbody>
          {rows.length > 0 && (
            <tfoot className="bg-gray-50 border-t-2 border-gray-200">
              <tr>
                <td colSpan={3} className="px-4 py-2.5 text-xs font-semibold text-gray-600 uppercase">Totals</td>
                <td className="px-4 py-2.5 font-semibold text-gray-900">{fmtJmd(totals.gross_revenue ?? 0)}</td>
                <td className="px-4 py-2.5 font-semibold text-amber-600">{fmtJmd(totals.discount_total ?? 0)}</td>
                <td className="px-4 py-2.5 font-bold text-gray-900">{fmtJmd(totals.net_revenue ?? 0)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

// ─── Discounts ────────────────────────────────────────────────────────────────
function DiscountsView({ data, generated, page, totalPages, onPageChange }: {
  data: any; generated: boolean;
  page: number; totalPages: number; onPageChange: (p: number) => void;
}) {
  if (!data) return <EmptyState generated={generated} />;

  const rows: DiscountReportRow[] = data.data ?? [];
  const totals = data.totals ?? {};

  return (
    <div className="space-y-3">
      {/* KPI cards — show even if 0 to confirm the report ran */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Discount Lines',    value: String(data.meta?.total ?? rows.length),      color: 'text-[#0997a4]'   },
          { label: 'Total Gross',       value: fmtJmd(totals.total_gross ?? 0),              color: 'text-gray-800'    },
          { label: 'Total Discounted',  value: fmtJmd(totals.total_discount_amount ?? 0),    color: 'text-amber-600'   },
          { label: 'Net After Disc.',   value: fmtJmd(totals.total_net ?? 0),                color: 'text-emerald-600' },
        ].map(c => (
          <div key={c.label} className="bg-white border border-gray-200 rounded-lg p-3">
            <p className="text-xs text-gray-500">{c.label}</p>
            <p className={`text-sm font-bold mt-0.5 ${c.color}`}>{c.value}</p>
          </div>
        ))}
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Date', 'Invoice', 'Patient', 'Item', 'Category', 'Qty', 'Line Gross', 'Disc. Type', 'Disc. Value', 'Disc. Amount', 'Net Line'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.length > 0 ? rows.map((r, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap">{fmtDate(r.date)}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-[#0997a4] whitespace-nowrap">{r.visit_ref}</td>
                  <td className="px-4 py-2.5 text-gray-900 whitespace-nowrap">{r.patient_name}</td>
                  <td className="px-4 py-2.5 font-medium text-gray-900">{r.item_name}</td>
                  <td className="px-4 py-2.5 text-xs text-gray-500">{r.category}</td>
                  <td className="px-4 py-2.5 text-gray-700">{r.quantity}</td>
                  <td className="px-4 py-2.5 text-gray-900">{fmtJmd(r.line_gross)}</td>
                  <td className="px-4 py-2.5">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${r.discount_type === 'percentage' ? 'bg-purple-50 text-purple-700' : 'bg-orange-50 text-orange-700'}`}>
                      {r.discount_type === 'percentage' ? '%' : 'Fixed'}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-gray-700">{r.discount_type === 'percentage' ? `${r.discount_value}%` : fmtJmd(r.discount_value)}</td>
                  <td className="px-4 py-2.5 font-medium text-amber-600">{fmtJmd(r.discount_amount)}</td>
                  <td className="px-4 py-2.5 font-semibold text-gray-900">{fmtJmd(r.net_line)}</td>
                </tr>
              )) : (
                <tr><td colSpan={11} className="px-4 py-8 text-center text-sm text-gray-400">No discounts found for this period.</td></tr>
              )}
            </tbody>
            {rows.length > 0 && (
              <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                <tr>
                  <td colSpan={6} className="px-4 py-2.5 text-xs font-semibold text-gray-600 uppercase">Totals</td>
                  <td className="px-4 py-2.5 font-semibold text-gray-900">{fmtJmd(totals.total_gross ?? 0)}</td>
                  <td colSpan={2} />
                  <td className="px-4 py-2.5 font-semibold text-amber-600">{fmtJmd(totals.total_discount_amount ?? 0)}</td>
                  <td className="px-4 py-2.5 font-bold text-gray-900">{fmtJmd(totals.total_net ?? 0)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
      <Pagination page={page} total={totalPages} onChange={onPageChange} />
    </div>
  );
}

// ─── Activity ─────────────────────────────────────────────────────────────────
function ActivityView({ data, generated, page, totalPages, onPageChange }: {
  data: any; generated: boolean;
  page: number; totalPages: number; onPageChange: (p: number) => void;
}) {
  if (!data) return <EmptyState generated={generated} />;
  const rows: any[] = Array.isArray(data?.data?.data) ? data.data.data : (Array.isArray(data?.data) ? data.data : []);
  const fmt = (d: string) => new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  return (
    <div className="space-y-3">
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              {['Date & Time', 'Type', 'Reference', 'User', 'Items', 'Total Qty', 'Location'].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.length > 0 ? rows.map((r: any) => {
              const items = r.items || [];
              return (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5 text-sm text-gray-900 whitespace-nowrap">{fmt(r.created_at || r.date)}</td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${r.type === 'receive' ? 'bg-green-50 text-green-700' : 'bg-[#50b3e1]/10 text-[#50b3e1]'}`}>
                      <i className={r.type === 'receive' ? 'ri-inbox-line' : 'ri-send-plane-line'} />{r.type === 'receive' ? 'Received' : 'Issued'}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs text-[#0997a4]">{r.reference_number || '—'}</td>
                  <td className="px-4 py-2.5 text-gray-900">{r.user?.name || '—'}</td>
                  <td className="px-4 py-2.5 text-gray-600 max-w-xs"><div className="truncate">{items.map((i: any) => i.item?.name || i.name || '').filter(Boolean).join(', ') || '—'}</div></td>
                  <td className="px-4 py-2.5 font-medium text-gray-900">{items.reduce((s: number, i: any) => s + (i.quantity || 0), 0)}</td>
                  <td className="px-4 py-2.5 text-gray-600">{r.location?.name || '—'}</td>
                </tr>
              );
            }) : (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-400">No activity found in this period.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <Pagination page={page} total={totalPages} onChange={onPageChange} />
    </div>
  );
}

// ─── Stock Batches ────────────────────────────────────────────────────────────
function StockBatchesView({ data, generated, page, totalPages, onPageChange }: {
  data: any; generated: boolean;
  page: number; totalPages: number; onPageChange: (p: number) => void;
}) {
  if (!data) return <EmptyState generated={generated} />;

  const rows: any[] = data.data ?? [];
  const summary = data.summary ?? {};
  const now = new Date();
  const soon = new Date(now.getTime() + 30 * 864e5);

  const expiryClass = (expiry: string | null) => {
    if (!expiry) return 'text-gray-400';
    const d = new Date(expiry + 'T00:00:00');
    if (d < now)  return 'text-red-600 font-semibold';
    if (d < soon) return 'text-amber-600 font-semibold';
    return 'text-emerald-600';
  };

  return (
    <div className="space-y-3">
      {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { label: 'Total Batches',    value: String(summary.total_batches   ?? 0),              color: 'text-[#0997a4]'   },
          { label: 'Unique Items',     value: String(summary.total_items     ?? 0),              color: 'text-gray-800'    },
          { label: 'Locations',        value: String(summary.total_locations ?? 0),              color: 'text-gray-800'    },
          { label: 'Total Remaining',  value: `${(summary.total_remaining ?? 0).toLocaleString()} units`, color: 'text-emerald-600' },
          { label: 'Stock Value',      value: fmtJmd(summary.total_value    ?? 0),              color: 'text-violet-600'  },
        ].map(c => (
          <div key={c.label} className="bg-white border border-gray-200 rounded-lg p-3">
            <p className="text-xs text-gray-500">{c.label}</p>
            <p className={`text-sm font-bold mt-0.5 ${c.color}`}>{c.value}</p>
          </div>
        ))}
      </div>

      {/* Expiry legend */}
      <div className="flex items-center gap-4 px-1">
        <span className="text-xs text-gray-500 font-medium">Expiry:</span>
        <span className="flex items-center gap-1 text-xs text-red-600"><i className="ri-error-warning-line" /> Expired</span>
        <span className="flex items-center gap-1 text-xs text-amber-600"><i className="ri-time-line" /> Expiring within 30 days</span>
        <span className="flex items-center gap-1 text-xs text-emerald-600"><i className="ri-checkbox-circle-line" /> Good</span>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Item', 'Category', 'Location', 'Batch #', 'Expiry Date', 'Received', 'Remaining', 'Unit Price', 'Stock Value'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.length > 0 ? rows.map((r: any, i: number) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5 font-medium text-gray-900 whitespace-nowrap">{r.item_name}</td>
                  <td className="px-4 py-2.5 text-xs text-gray-500">{r.category}</td>
                  <td className="px-4 py-2.5 text-gray-700 whitespace-nowrap">{r.location_name}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-[#0997a4]">{r.batch_number}</td>
                  <td className={`px-4 py-2.5 text-xs whitespace-nowrap ${expiryClass(r.expiry_date)}`}>
                    {r.expiry_date ? fmtDate(r.expiry_date) : '—'}
                  </td>
                  <td className="px-4 py-2.5 text-gray-600">{r.quantity_received.toLocaleString()}</td>
                  <td className="px-4 py-2.5">
                    <span className={`font-semibold ${r.quantity_remaining === 0 ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                      {r.quantity_remaining.toLocaleString()}
                    </span>
                    {r.quantity_remaining > 0 && r.quantity_received > 0 && (
                      <span className="ml-1.5 text-xs text-gray-400">
                        ({Math.round((r.quantity_remaining / r.quantity_received) * 100)}%)
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-gray-700">{fmtJmd(r.unit_price)}</td>
                  <td className="px-4 py-2.5 font-medium text-gray-900">{fmtJmd(r.stock_value)}</td>
                </tr>
              )) : (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-sm text-gray-400">No batch data found. Try adjusting your filters.</td></tr>
              )}
            </tbody>
            {rows.length > 0 && (
              <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                <tr>
                  <td colSpan={5} className="px-4 py-2.5 text-xs font-semibold text-gray-600 uppercase">Totals (this page)</td>
                  <td className="px-4 py-2.5 font-semibold text-gray-700">{rows.reduce((s: number, r: any) => s + r.quantity_received, 0).toLocaleString()}</td>
                  <td className="px-4 py-2.5 font-semibold text-gray-900">{rows.reduce((s: number, r: any) => s + r.quantity_remaining, 0).toLocaleString()}</td>
                  <td />
                  <td className="px-4 py-2.5 font-bold text-gray-900">{fmtJmd(rows.reduce((s: number, r: any) => s + r.stock_value, 0))}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
      <Pagination page={page} total={totalPages} onChange={onPageChange} />
    </div>
  );
}

// ─── Utilization ──────────────────────────────────────────────────────────────
function UtilizationView({ data, view, generated, page, totalPages, onPageChange }: {
  data: any; view: 'summary' | 'detail'; generated: boolean;
  page: number; totalPages: number; onPageChange: (p: number) => void;
}) {
  if (!data) return <EmptyState generated={generated} />;

  if (view === 'summary') {
    const d = data as UtilizationReport;
    const items = d.items ?? [];
    const allLocs = Array.from(new Set(items.flatMap(i => i.locations.map(l => l.location_name)))).sort();
    const getQty = (item: typeof items[0], loc: string) => item.locations.find(l => l.location_name === loc)?.total_issued ?? 0;
    return (
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider sticky left-0 bg-gray-50">Item</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Category</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Unit</th>
                {allLocs.map(loc => <th key={loc} className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">{loc}</th>)}
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
              {items.length > 0 ? items.map(item => (
                    <tr key={item.item_id} className="hover:bg-gray-50">
                      <td className="px-4 py-2.5 font-medium text-gray-900 sticky left-0 bg-white">{item.item_name}</td>
                  <td className="px-4 py-2.5 text-xs text-gray-500">{item.category ?? '—'}</td>
                  <td className="px-4 py-2.5 text-xs text-gray-500">{item.item_unit}</td>
                  {allLocs.map(loc => {
                        const qty = getQty(item, loc);
                    return <td key={loc} className="px-4 py-2.5 text-right">{qty > 0 ? <span className="font-medium text-gray-900">{qty}</span> : <span className="text-gray-300">—</span>}</td>;
                      })}
                      <td className="px-4 py-2.5 text-right font-semibold text-gray-900">{item.grand_total}</td>
                    </tr>
                  )) : (
                <tr><td colSpan={3 + allLocs.length + 1} className="px-4 py-8 text-center text-sm text-gray-400">No utilization data in this period.</td></tr>
                  )}
                </tbody>
            {items.length > 0 && (
                  <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                    <tr>
                  <td colSpan={3} className="px-4 py-2.5 text-xs font-semibold text-gray-600 uppercase">Totals</td>
                  {allLocs.map(loc => <td key={loc} className="px-4 py-2.5 text-right font-semibold text-gray-700">{items.reduce((s, item) => s + getQty(item, loc), 0)}</td>)}
                  <td className="px-4 py-2.5 text-right font-bold text-gray-900">{items.reduce((s, item) => s + item.grand_total, 0)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
    );
  }

  const rows: UtilizationDetailRow[] = data.data ?? [];
  return (
    <div className="space-y-3">
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                {['Date', 'Item', 'Category', 'Qty', 'Location', 'Patient', 'MRN', 'Visit', 'Type'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
              {rows.length > 0 ? rows.map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap">{r.date}</td>
                  <td className="px-4 py-2.5 font-medium text-gray-900">{r.item_name}</td>
                  <td className="px-4 py-2.5 text-xs text-gray-500">{r.category ?? '—'}</td>
                  <td className="px-4 py-2.5 font-medium text-gray-900">{r.quantity} <span className="text-gray-400 text-xs font-normal">{r.item_unit}</span></td>
                  <td className="px-4 py-2.5 text-xs text-gray-700 whitespace-nowrap">{r.location_name}</td>
                  <td className="px-4 py-2.5 text-gray-900">{r.patient_name}</td>
                  <td className="px-4 py-2.5 text-xs text-gray-500">{r.patient_identifier || '—'}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-[#0997a4]">{r.visit_reference}</td>
                  <td className="px-4 py-2.5"><span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${r.visit_type === 'inpatient' ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>{r.visit_type}</span></td>
                    </tr>
                  )) : (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-sm text-gray-400">No records in this period.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
      <Pagination page={page} total={totalPages} onChange={onPageChange} />
    </div>
  );
}
